/**
 * OperatorKpiCard — uppercase label, large value, delta with arrow,
 * optional progress bar.
 */
import { tokens } from "./tokens";

export interface OperatorKpiCardProps {
  label: string;
  value: string;
  delta?: { value: string; direction: "up" | "down" | "flat"; tone?: "good" | "warn" | "bad" | "neutral" };
  progress?: { pct: number; color?: string }; // 0..100
  hint?: string;
}

export default function OperatorKpiCard({ label, value, delta, progress, hint }: OperatorKpiCardProps) {
  const deltaColor = delta
    ? delta.tone === "bad"
      ? tokens.color.danger
      : delta.tone === "warn"
        ? tokens.color.amber
        : delta.tone === "neutral"
          ? tokens.color.textMuted
          : tokens.color.success
    : tokens.color.textMuted;

  const arrow = delta?.direction === "down" ? "▼" : delta?.direction === "flat" ? "→" : "▲";

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: "8px 12px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontFamily: tokens.font.body,
        minHeight: 76,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: tokens.color.textMuted,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: tokens.font.display,
          fontWeight: 700,
          fontSize: 26,
          lineHeight: 1.1,
          color: tokens.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {delta && (
        <div style={{ fontSize: 12, color: deltaColor, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
          <span>{arrow}</span>
          <span>{delta.value}</span>
        </div>
      )}
      {hint && !delta && (
        <div style={{ fontSize: 12, color: tokens.color.textMuted }}>{hint}</div>
      )}
      {progress && (
        <div
          style={{
            marginTop: 8,
            height: 4,
            background: "#EFEFEE",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, progress.pct))}%`,
              height: "100%",
              background: progress.color || tokens.color.black,
              transition: "width 0.3s",
            }}
          />
        </div>
      )}
    </div>
  );
}
